import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import {
  DEFAULT_PAGE_LIMIT,
  type ClauseMatchListItem,
  type ClauseMatchListResponse,
  type ClauseMatchQueue,
  type ClauseMatchState,
  type LibraryClause,
  type ManageClauseRequest,
} from '@clm/shared-types';

import { fetchClauseMatches } from '../../api/clause-matches';
import { manageClause } from '../../api/clauses';
import { ApiError } from '../../api/errors';
import { classifyApiError } from '../../api/problem';
import { mayAttempt } from '../../auth/permissions';
import { useIdentity } from '../../auth/useIdentity';
import { ContractTypeTag } from '../../components/ContractTypeTag';
import { DataTable, type DataTableColumn } from '../../components/DataTable';
import { ErrorNotice } from '../../components/ErrorNotice';
import { RowActions, WriteNotice, type Problem, type Verb } from './RowActions';

/**
 * Match review — paragraph matches mode (CLM-60; FR-28, CR-01, NFR-06, NFR-13).
 *
 * The FR-28 per-paragraph correction queue that CLM-34 built at the API:
 * `GET /v1/clause-matches` serves the MEDIUM (`pending_match_review`) and LOW
 * (`bespoke_candidates`) queues, scoped server-side on `root_contract_key`, and
 * `manage-clause resolve_clause_match` records the human decision with its
 * `HumanVerification` audit row. Each decision here is one labelled
 * `(paragraph, clause, verdict)` triple — the calibration labels runbook J2
 * collects before `match-clauses --report` (J3) can pick the thresholds.
 *
 * ## Three verbs, one body
 *
 * Confirm, Correct and Bespoke all post `{ action: 'resolve_clause_match',
 * contract_clause_id, clause_code, match_state, deviation_note }`; they differ
 * only in the last three fields. Nothing here invents a `resolution` or a lock
 * token — the API's R1 lock answers an already-reviewed row with 409 and the
 * list is re-fetched.
 *
 * ## Never patch a row
 *
 * After every write the first page of the current queue is re-fetched: the
 * totals are server-counted and a resolved row leaves the queue, so the only
 * honest row is the server's post-image. That is also why paging is a keyset
 * `Load more` (D-60-5) and not Next/Previous — a "previous page" of a queue
 * that rows leave as they are resolved is stale by construction.
 *
 * ## No client-side filtering
 *
 * The `queue` is the API's predicate; `confidence_band` is shown, never
 * filtered on here. The clause picker narrows the *library* it fetched, not
 * this queue (D-60-4).
 */

interface ListState {
  readonly rows: readonly ClauseMatchListItem[];
  readonly totals: ClauseMatchListResponse['totals'] | null;
  readonly next_cursor: string | null;
  readonly busy: boolean;
  /** First-page failure: nothing is loaded, so the notice replaces the table. */
  readonly problem: Problem | null;
  /** `Load more` failure: the loaded rows stay; the notice sits under the table with its own Retry. */
  readonly load_more_problem: Problem | null;
}

interface Confirming {
  readonly contract_clause_id: string;
  readonly verb: Verb;
}

type WritePhase =
  | { readonly kind: 'idle' }
  | { readonly kind: 'busy' }
  | { readonly kind: 'done'; readonly audit_id: string; readonly word: string }
  | { readonly kind: 'problem'; readonly problem: Problem };

const INITIAL: ListState = { rows: [], totals: null, next_cursor: null, busy: true, problem: null, load_more_problem: null };

const QUEUE_LABELS: Readonly<Record<ClauseMatchQueue, string>> = {
  pending_match_review: 'Pending review (MEDIUM)',
  bespoke_candidates: 'Bespoke candidates (LOW)',
};

export const ROLE_HINT = 'Needs the Legal reviewer or Admin role, which your sign-in does not show';

function toProblem(error: unknown): Problem {
  const body = error instanceof ApiError ? (error.body as { field_errors?: unknown } | null) : null;
  const fieldErrors = Array.isArray(body?.field_errors)
    ? (body.field_errors as { path?: unknown; message?: unknown }[])
        .filter((entry) => typeof entry.path === 'string' && typeof entry.message === 'string')
        .map((entry) => ({ path: entry.path as string, message: entry.message as string }))
    : [];
  return {
    kind: classifyApiError(error),
    detail: error instanceof ApiError ? error.message : null,
    correlation_id: error instanceof ApiError ? error.correlation_id : null,
    field_errors: fieldErrors,
  };
}


/** The roles the gateway grants `clause:manage` to — a display hint, never the gate. */
const REVIEW_ROLES: readonly string[] = ['legal_reviewer', 'admin'];

export function ParagraphMatches() {
  const { identity } = useIdentity();
  /* D-82-1: a sign-in that names no role is not dimmed — the API decides. */
  const canReview = mayAttempt(identity, REVIEW_ROLES);
  /*
   * CLM-112 — `?contract_key=` narrows both queues to one contract: the base-MSA
   * deviation report links here for what it could not compare. The API already
   * accepts the filter and applies the caller's scope on top of it (NFR-06);
   * an empty value is no filter.
   */
  const [searchParams] = useSearchParams();
  const contractKey = searchParams.get('contract_key')?.trim() || null;

  const [queue, setQueue] = useState<ClauseMatchQueue>('pending_match_review');
  const [state, setState] = useState<ListState>(INITIAL);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [confirming, setConfirming] = useState<Confirming | null>(null);
  const [note, setNote] = useState('');
  const [picked, setPicked] = useState<LibraryClause | null>(null);
  const [correctState, setCorrectState] = useState<ClauseMatchState>('Standard');
  const [write, setWrite] = useState<WritePhase>({ kind: 'idle' });
  const [writesForbidden, setWritesForbidden] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  /* First page of the current queue — on mount, on a queue change, after every write, on Retry. */
  useEffect(() => {
    const controller = new AbortController();
    abortRef.current?.abort();
    abortRef.current = controller;
    setState((previous) => ({ ...previous, busy: true }));

    void (async () => {
      try {
        const response = await fetchClauseMatches(
          { queue, ...(contractKey === null ? {} : { contract_key: contractKey }), page: { limit: DEFAULT_PAGE_LIMIT } },
          controller.signal,
        );
        if (controller.signal.aborted) return;
        setState({
          rows: response.items,
          totals: response.totals,
          next_cursor: response.page.next_cursor,
          busy: false,
          problem: null,
          load_more_problem: null,
        });
      } catch (error) {
        if (controller.signal.aborted) return;
        setState({ rows: [], totals: null, next_cursor: null, busy: false, problem: toProblem(error), load_more_problem: null });
      }
    })();

    return () => {
      controller.abort();
    };
  }, [queue, reloadNonce, contractKey]);

  const reload = useCallback(() => {
    setReloadNonce((n) => n + 1);
  }, []);

  const closeConfirm = useCallback(() => {
    setConfirming(null);
    setNote('');
    setPicked(null);
    setCorrectState('Standard');
  }, []);

  /** Keyset append (D-60-5): the next page of the same queue, added under the loaded rows. */
  const loadMore = useCallback(() => {
    const cursor = state.next_cursor;
    if (cursor === null) return;
    const controller = new AbortController();
    abortRef.current?.abort();
    abortRef.current = controller;
    setState((previous) => ({ ...previous, busy: true }));
    void (async () => {
      try {
        const response = await fetchClauseMatches(
          { queue, ...(contractKey === null ? {} : { contract_key: contractKey }), page: { limit: DEFAULT_PAGE_LIMIT, cursor } },
          controller.signal,
        );
        if (controller.signal.aborted) return;
        setState((previous) => ({
          rows: [...previous.rows, ...response.items],
          totals: response.totals,
          next_cursor: response.page.next_cursor,
          busy: false,
          problem: null,
          load_more_problem: null,
        }));
      } catch (error) {
        if (controller.signal.aborted) return;
        /* The rows already on screen stay; only the appended page failed. */
        setState((previous) => ({ ...previous, busy: false, load_more_problem: toProblem(error) }));
      }
    })();
  }, [queue, state.next_cursor, contractKey]);

  const runWrite = useCallback(
    (body: Extract<ManageClauseRequest, { action: 'resolve_clause_match' }>, word: string) => {
      setWrite({ kind: 'busy' });
      void (async () => {
        try {
          const response = await manageClause(body);
          setWrite({ kind: 'done', audit_id: response.audit.audit_id, word });
          closeConfirm();
          reload();
        } catch (error) {
          const problem = toProblem(error);
          setWrite({ kind: 'problem', problem });
          if (problem.kind === 'forbidden') {
            setWritesForbidden(true);
            closeConfirm();
          } else if (problem.kind === 'conflict' || problem.kind === 'not_found') {
            closeConfirm();
            reload();
          }
          /* `validation` and anything else keep the confirmation open — no re-fetch. */
        }
      })();
    },
    [closeConfirm, reload],
  );

  const verbsDisabled = !canReview || writesForbidden || write.kind === 'busy';

  const columns: readonly DataTableColumn<ClauseMatchListItem>[] = [
    {
      id: 'contract',
      header: 'Contract',
      cell: (row) => (
        <>
          <code>{row.contract_key}</code> ·{' '}
          <ContractTypeTag type={row.contract_type} confirmation={row.contract_type_confirmation} /> · {row.sow_number ?? '—'}
          <br />
          <span className="mut">¶ {row.paragraph_ref ?? '—'}</span>
        </>
      ),
    },
    {
      id: 'paragraph',
      header: 'Paragraph',
      cell: (row) => (
        <div className="mr-excerpts">
          <div>
            <span className="mr-excerpt-label">Contract paragraph</span>
            <blockquote className="mr-excerpt" title={row.paragraph_excerpt}>
              {row.paragraph_excerpt}
            </blockquote>
          </div>
          {row.candidate === null ? null : (
            <div>
              <span className="mr-excerpt-label">Library master text</span>
              <blockquote className="mr-excerpt master" title={row.candidate.master_text_excerpt}>
                {row.candidate.master_text_excerpt}
              </blockquote>
            </div>
          )}
        </div>
      ),
    },
    {
      id: 'candidate',
      header: 'Proposed clause',
      cell: (row) =>
        row.candidate === null ? (
          <span className="mut">— bespoke</span>
        ) : (
          <>
            <code>{row.candidate.clause_code}</code> v{row.candidate.clause_version_no}
            <br />
            {row.candidate.title}
          </>
        ),
    },
    {
      id: 'band',
      header: 'Band / state',
      cell: (row) => (
        <>
          <span className="tag band" data-band={row.confidence_band ?? 'none'}>
            {row.confidence_band ?? '—'}
          </span>{' '}
          {row.match_state ?? 'unadjudicated'}
          {row.settled_by_near_exact_match ? <> · <span className="tag ok">near-exact</span></> : null}
          {row.match_reasoning === null ? null : (
            <details className="mr-reasoning">
              <summary>Why the matcher thinks so</summary>
              <p>{row.match_reasoning}</p>
            </details>
          )}
        </>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: (row) => (
        <RowActions
          row={row}
          confirming={confirming?.contract_clause_id === row.contract_clause_id ? confirming.verb : null}
          disabled={verbsDisabled}
          roleHint={canReview ? null : ROLE_HINT}
          note={note}
          picked={picked}
          correctState={correctState}
          fieldErrors={write.kind === 'problem' && write.problem.kind === 'validation' ? write.problem.field_errors : []}
          onAsk={(verb) => {
            closeConfirm();
            setWrite({ kind: 'idle' });
            setConfirming({ contract_clause_id: row.contract_clause_id, verb });
          }}
          onCancel={closeConfirm}
          onNote={setNote}
          onPick={setPicked}
          onState={setCorrectState}
          onConfirm={(body, word) => {
            runWrite(body, word);
          }}
        />
      ),
    },
  ];

  return (
    <div className="card">
      <h3>Paragraph matches</h3>
      <p>
        Each row is one paragraph the matcher could not settle on its own. Confirm the proposed clause, correct it to
        another library clause, or declare the paragraph bespoke. Every decision is recorded with an audit row and the
        queue is re-read from the server. The queue is sent to the API; nothing is filtered in this browser.
      </p>

      <div className="vfilters">
        <label className="check">
          Queue
          <select
            aria-label="Queue"
            value={queue}
            onChange={(event) => {
              closeConfirm();
              setWrite({ kind: 'idle' });
              setQueue(event.target.value as ClauseMatchQueue);
            }}
          >
            {(Object.keys(QUEUE_LABELS) as ClauseMatchQueue[]).map((value) => (
              <option key={value} value={value}>
                {QUEUE_LABELS[value]}
              </option>
            ))}
          </select>
        </label>
        {contractKey === null ? null : (
          <span className="fl" data-testid="paragraph-matches-contract-filter">
            Only <code>{contractKey}</code>’s paragraphs{' '}
            <Link to="/clauses/match-review" className="btn sm qt">
              Show every contract
            </Link>
          </span>
        )}
      </div>

      {state.totals ? (
        <div className="totals">
          <div className="total">
            <span>Pending review (MEDIUM)</span>
            <b>{state.totals.pending_match_review}</b>
          </div>
          <div className="total" data-tone="out">
            <span>Bespoke candidates (LOW)</span>
            <b>{state.totals.bespoke_candidates}</b>
          </div>
        </div>
      ) : null}

      {canReview && !writesForbidden ? null : (
        <p className="mut" role={writesForbidden ? 'status' : undefined}>
          Match review is for CEQ-Legal (<code>legal_reviewer</code>) and Admin.{' '}
          {writesForbidden ? 'Your sign-in was accepted; your role was not.' : `${ROLE_HINT}; the API decides on every call.`}
        </p>
      )}

      {write.kind === 'done' ? (
        <p role="status" className="mut">
          Recorded · audit {write.audit_id} · {write.word}
        </p>
      ) : null}
      {write.kind === 'problem' ? <WriteNotice problem={write.problem} /> : null}

      {state.problem ? (
        <ErrorNotice
          kind={state.problem.kind}
          detail={state.problem.detail}
          correlation_id={state.problem.correlation_id}
          action={
            state.problem.kind === 'not_implemented' ? (
              <p className="mut">
                The endpoint is <code>GET /v1/clause-matches</code>; an empty queue here would be a false claim.
              </p>
            ) : state.problem.kind === 'forbidden' ? (
              <p className="mut">
                Match review is for CEQ-Legal (<code>legal_reviewer</code>) and Admin. Your sign-in was accepted; your
                role was not.
              </p>
            ) : state.problem.kind === 'unknown' ? (
              <button type="button" className="btn" onClick={reload}>
                Retry
              </button>
            ) : undefined
          }
        />
      ) : (
        <>
          <DataTable
            caption="Paragraph matches"
            columns={columns}
            rows={state.rows}
            rowKey={(row) => row.contract_clause_id}
            isCurrent={(row) => row.contract_clause_id === confirming?.contract_clause_id}
            busy={state.busy}
            empty={
              <p>
                <strong>
                  {queue === 'pending_match_review'
                    ? 'No paragraphs are waiting for review in your scope.'
                    : 'No bespoke candidates in your scope.'}
                </strong>{' '}
                Rows appear after <code>match-clauses</code> has run (B-33, B-34).
              </p>
            }
          />
          {state.next_cursor === null ? null : (
            <button type="button" className="btn" disabled={state.busy} onClick={loadMore}>
              Load more
            </button>
          )}
          {state.load_more_problem === null ? null : (
            <ErrorNotice
              kind={state.load_more_problem.kind}
              detail={state.load_more_problem.detail}
              correlation_id={state.load_more_problem.correlation_id}
              action={
                <p className="mut">
                  The rows above are still the ones loaded so far; the next page was not added.{' '}
                  {state.next_cursor === null ? null : (
                    <button type="button" className="btn sm" disabled={state.busy} onClick={loadMore}>
                      Retry
                    </button>
                  )}
                </p>
              }
            />
          )}
        </>
      )}
    </div>
  );
}
